import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

type NumberFormatterOptions = Intl.NumberFormatOptions & {
  locale?: Intl.LocalesArgument;
};

const DEFAULT_FORMATTER_OPTIONS = {
  CURRENCY: {
    currency: 'EUR',
    style: 'currency',
  },
} satisfies { [key: string]: NumberFormatterOptions };

export const useNumberFormatter = (options?: NumberFormatterOptions) => {
  const { i18n } = useTranslation();

  const locale = options?.locale ?? i18n.language;

  // only create new formatted if a primitive option changes instead of the object reference
  return useMemo(
    () =>
      new Intl.NumberFormat(locale, {
        compactDisplay: options?.compactDisplay,
        currency: options?.currency,
        currencyDisplay: options?.currencyDisplay,
        currencySign: options?.currencySign,
        localeMatcher: options?.localeMatcher,
        maximumFractionDigits: options?.maximumFractionDigits,
        maximumSignificantDigits: options?.maximumSignificantDigits,
        minimumFractionDigits: options?.minimumFractionDigits,
        minimumIntegerDigits: options?.minimumIntegerDigits,
        minimumSignificantDigits: options?.minimumSignificantDigits,
        notation: options?.notation,
        numberingSystem: options?.numberingSystem,
        roundingIncrement: options?.roundingIncrement,
        roundingMode: options?.roundingMode,
        roundingPriority: options?.roundingPriority,
        signDisplay: options?.signDisplay,
        style: options?.style,
        trailingZeroDisplay: options?.trailingZeroDisplay,
        unit: options?.unit,
        unitDisplay: options?.unitDisplay,
        useGrouping: options?.useGrouping,
      }),
    [
      locale,
      options?.compactDisplay,
      options?.currency,
      options?.currencyDisplay,
      options?.currencySign,
      options?.localeMatcher,
      options?.maximumFractionDigits,
      options?.maximumSignificantDigits,
      options?.minimumFractionDigits,
      options?.minimumIntegerDigits,
      options?.minimumSignificantDigits,
      options?.notation,
      options?.numberingSystem,
      options?.roundingIncrement,
      options?.roundingMode,
      options?.roundingPriority,
      options?.signDisplay,
      options?.style,
      options?.trailingZeroDisplay,
      options?.unit,
      options?.unitDisplay,
      options?.useGrouping,
    ],
  );
};

export const useCurrencyFormatter = (options?: NumberFormatterOptions) =>
  useNumberFormatter({
    ...DEFAULT_FORMATTER_OPTIONS.CURRENCY,
    ...options,
  });
